import * as React from 'react'
import { Text, TouchableOpacity, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import Animators from '@components/root/Animators'
import Info from '../form/Info'
import SignaturePad from '@components/signatures/SignaturePad'
import Root from '@components/root/Root'
import Header from '@components/header/Header'

import scheme from '../scheme'

import useI18n from '@i18n/useI18n'

// web render with both form and signature
const FormSignatureLayout = ({ title, name, type, form, onForm, onSceneChange, onBack }: SignatureLayoutProps) => {
  const pad = React.useRef<React.ElementRef<typeof SignaturePad>>(null)

  const i18n = useI18n()

  const [valid, setValid] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)

  const [local, setLocal] = React.useState(form)

  React.useEffect(() => {
    scheme.info
      .validate(local)
      .then(() => setValid(true))
      .catch(() => setValid(false))
  }, [local])

  const submit = () => {
    if (pad.current) {
      pad.current.save()
    }
  }

  const onLocalForm = (vals: Partial<SignatureForm>) => {
    setLocal({ ...local, ...vals })
    return Promise.resolve()
  }

  const onSave = (image: string) => {
    onForm({ ...local, signature: image })
      .then(() => onSceneChange('GLOBAL'))
      .finally(() => setSubmitting(true))
  }

  return (
    <Root fixed header={title} onLeft={onBack}>
      <Header
        title={i18n.t('screens.edlSignature.signatureTitle', { type: i18n.t(`screens.edlRecap.${type}`) })}
        description={name}
      />

      <ScrollView bounces={false} contentContainerStyle={{ flexGrow: 1 }}>
        <Description>
          {i18n.t('screens.edlSignature.signatureFormDescription', { type: i18n.t(`screens.edlRecap.${type}`) })}
        </Description>

        <Line>
          <Column>
            <Animators>
              <Info form={local} onForm={onLocalForm} />
            </Animators>
          </Column>

          <Column>
            <Title>{i18n.t('screens.edlSignature.stepTitle')}</Title>
            <Signature>
              <SignaturePad ref={pad} onSave={onSave} />
              <Clear activeOpacity={0.8} onPress={() => pad.current?.reset()}>
                <ClearText>{i18n.t('screens.edlSignature.clear')}</ClearText>
              </Clear>
            </Signature>
          </Column>
        </Line>
      </ScrollView>

      <Buttons>
        <ButtonContainer>
          <Button
            textOnly
            textColor={theme.colors.primary}
            label={i18n.t('common.cancel')}
            status="active"
            onClick={() => onSceneChange('GLOBAL')}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button
            label={i18n.t('common.validate')}
            status={valid ? (submitting ? 'loading' : 'active') : 'disabled'}
            onClick={submit}
          />
        </ButtonContainer>
      </Buttons>
    </Root>
  )
}

export default FormSignatureLayout

const Line = styled(View)`
  flex: 1;
  flex-direction: row;
  margin: 10px 40px 40px;
`

const Column = styled(View)`
  flex: 1;
  flex-direction: column;
  margin: 20px;
`

const Buttons = styled(View)`
  flex-direction: row;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  width: 275px;
  margin: 12px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 8px;
`

const Description = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.tertiary};
  text-align: center;
  margin: 40px 40px 10px;
`

const Signature = styled(View)`
  height: 300px;
`

const Clear = styled(TouchableOpacity)`
  position: absolute;
  right: 0;
  top: 0;
  border-radius: 4px;
  margin: 10px;
  padding: 8px;
  background-color: ${(props) => props.theme.colors.primaryFocused};
`

const ClearText = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
`
